import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Signup.css';
import { getApiUrl } from '../config/api';
import authFetch, { parseToken } from '../utils/authFetch';
import { roleLabel } from '../utils/roles';
import switchOrganization from '../utils/switchOrganization';

// Accept an organization invitation as an already-signed-in user. Reached after
// logging in from an invite link (/login?invite=...) which forwards here.
const InviteAccept = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // verifying | ready | verifyError | joining | joined | error | switching
  const [status, setStatus] = useState('verifying');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [accepted, setAccepted] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [currentOrgName, setCurrentOrgName] = useState('');

  useEffect(() => {
    if (!token) {
      setErrorMsg('Invitation token is missing.');
      setStatus('verifyError');
      return;
    }

    // Accepting requires a live session. Treat a missing OR expired access token
    // the same: send the visitor to log in with the token preserved so the
    // accept flow resumes here afterwards (an expired token would otherwise fail
    // the first authFetch and drop them on a plain /login).
    const payload = parseToken(localStorage.getItem('access_token'));
    if (!payload || Date.now() >= payload.exp * 1000) {
      navigate(`/login?invite=${encodeURIComponent(token)}`);
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(
          getApiUrl(`/api/v1/organization/invite/verify?token=${encodeURIComponent(token)}`)
        );
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setInviteInfo(data);
          setStatus('ready');
        } else {
          setErrorMsg(data.error || 'Invalid or expired invitation link.');
          setStatus('verifyError');
        }
      } catch (err) {
        console.error('Error verifying invite:', err);
        setErrorMsg('Unable to verify invitation. Please try again.');
        setStatus('verifyError');
      }
    };

    // Best-effort: resolve the org the user is currently in so the "Stay" action
    // can name it.
    const resolveCurrentOrg = async () => {
      try {
        const res = await authFetch(getApiUrl('/api/v1/organization/list'));
        if (res.ok) {
          const data = await res.json();
          const activeOrgId = parseToken(localStorage.getItem('access_token'))?.org_id ?? null;
          const cur = (data.organizations || []).find((o) => o.organization_id === activeOrgId);
          if (cur) setCurrentOrgName(cur.org_name);
        }
      } catch (err) {
        console.error('Error resolving current organization:', err);
      }
    };

    verify();
    resolveCurrentOrg();
  }, [token, navigate]);

  const handleJoin = async () => {
    try {
      setStatus('joining');
      setErrorMsg('');
      const res = await authFetch(getApiUrl('/api/v1/organization/invite/accept'), {
        method: 'POST',
        body: JSON.stringify({ invite_token: token }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        setAccepted(data);
        setStatus('joined');
      } else {
        // Surface the API's distinct states with their own messages:
        // 400 expired/invalid, 403 wrong email / revoked, 409 already a member.
        setErrorMsg(data.error || 'Could not accept this invitation.');
        setStatus('error');
      }
    } catch (err) {
      console.error('Error accepting invite:', err);
      setErrorMsg('An error occurred. Please try again.');
      setStatus('error');
    }
  };

  const handleSwitch = async () => {
    try {
      setStatus('switching');
      await switchOrganization(accepted.organization_id);
      // Full navigation so every org-scoped view loads under the new context.
      window.location.assign('/dashboard');
    } catch (err) {
      console.error('Error switching organization:', err);
      setErrorMsg('Could not switch organizations. Please try again.');
      setStatus('joined');
    }
  };

  const shell = (children) => (
    <div className="page-container">
      <div className="signup-wrapper">
        <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
        <div className="signup-box">{children}</div>
      </div>
    </div>
  );

  if (status === 'verifying') {
    return shell(<p className="text-center text-gray-600 py-8">Verifying invitation...</p>);
  }

  if (status === 'verifyError') {
    return shell(
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Invalid Invitation</h2>
        <p className="text-gray-600">{errorMsg}</p>
        <button
          className="mt-6 px-6 py-2 bg-green-700 text-white rounded hover:bg-green-800"
          onClick={() => navigate('/dashboard')}
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  if (status === 'joined') {
    return shell(
      <div className="text-center py-6">
        <div className="text-5xl mb-4">✓</div>
        <h2 className="text-xl font-semibold text-green-700">
          You've joined {accepted?.org_name}
        </h2>
        <p className="text-gray-600 mt-2">
          You're now a {roleLabel(accepted?.role)} of {accepted?.org_name}.
        </p>

        {errorMsg && <p className="error-message mt-4">{errorMsg}</p>}

        <div className="mt-6 flex flex-col gap-3">
          <button
            className="w-full p-2 bg-green-700 text-white rounded hover:bg-green-800"
            onClick={handleSwitch}
          >
            Switch to {accepted?.org_name} now
          </button>
          <button
            className="w-full p-2 bg-transparent text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
            onClick={() => navigate('/dashboard')}
          >
            {currentOrgName ? `Stay in ${currentOrgName}` : 'Stay in your current organization'}
          </button>
        </div>
      </div>
    );
  }

  if (status === 'switching') {
    return shell(
      <p className="text-center text-gray-600 py-8">Switching to {accepted?.org_name}...</p>
    );
  }

  // 'ready', 'joining', and 'error' all render the join prompt.
  return shell(
    <div className="py-2">
      <h2 className="text-xl font-semibold text-gray-800">Join {inviteInfo?.org_name}</h2>
      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
        You've been invited to join <strong>{inviteInfo?.org_name}</strong> as
        a <strong>{roleLabel(inviteInfo?.role)}</strong>.
      </div>

      {status === 'error' && <p className="error-message mt-4">{errorMsg}</p>}

      <button
        className="mt-6 w-full p-2 bg-green-700 text-white rounded hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={handleJoin}
        disabled={status === 'joining'}
      >
        {status === 'joining' ? 'Joining...' : `Join ${inviteInfo?.org_name}`}
      </button>
      <button
        className="mt-3 w-full p-2 bg-transparent text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
        onClick={() => navigate('/dashboard')}
      >
        Cancel
      </button>
    </div>
  );
};

export default InviteAccept;
